import { z } from 'zod'

// Recadrage d'une photo de document. Sur le téléphone, les bords de la page sont cherchés et
// ses quatre coins proposés ; la personne les ajuste ou garde la photo entière. Les coins
// accompagnent la photo, qui part intacte ; le serveur redresse la page à l'assemblage
// (perspective corrigée, page rectangulaire). Géométrie pure, commune au téléphone (aperçu)
// et au serveur (assemblage) ; le worker d'image, autonome, n'en reçoit que l'homographie.

/**
 * Coins de la page dans la photo telle qu'affichée (orientation EXIF appliquée), en fraction
 * de sa largeur et de sa hauteur, x puis y : haut gauche, haut droit, bas droit, bas gauche.
 */
export type Cadre = readonly [number, number, number, number, number, number, number, number]
export type Point = readonly [number, number]
/** Matrice 3 × 3 par lignes, dernier terme 1. */
export type Homographie = readonly [
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
  number,
]

export const CADRE_ENTIER: Cadre = [0, 0, 1, 0, 1, 1, 0, 1]
/** Aire minimale d'une page recadrée, en fraction de la photo. */
export const AIRE_MIN_CADRE = 0.04
/** Chaque coin à moins de cet écart de celui de la photo : la photo entière, gardée telle quelle. */
export const MARGE_CADRE_ENTIER = 0.01

const CARRE: readonly Point[] = [
  [0, 0],
  [1, 0],
  [1, 1],
  [0, 1],
]

export function coinsDe(c: Cadre): [Point, Point, Point, Point] {
  return [
    [c[0], c[1]],
    [c[2], c[3]],
    [c[4], c[5]],
    [c[6], c[7]],
  ]
}

/**
 * Quatre points dans l'ordre d'un cadre : tournés dans le sens des aiguilles d'une montre
 * autour de leur centre, en partant du plus proche du haut gauche (x + y le plus petit).
 */
export function ordonnerCoins(points: readonly Point[]): Cadre {
  const cx = points.reduce((s, p) => s + p[0], 0) / points.length
  const cy = points.reduce((s, p) => s + p[1], 0) / points.length
  // y vers le bas : l'angle croissant tourne dans le sens des aiguilles d'une montre.
  const tries = [...points].sort(
    (a, b) => Math.atan2(a[1] - cy, a[0] - cx) - Math.atan2(b[1] - cy, b[0] - cx),
  )
  let debut = 0
  for (let k = 1; k < tries.length; k++) {
    const p = tries[k]!
    const d = tries[debut]!
    if (p[0] + p[1] < d[0] + d[1] || (p[0] + p[1] === d[0] + d[1] && p[0] < d[0])) debut = k
  }
  const o = [...tries.slice(debut), ...tries.slice(0, debut)]
  return [o[0]![0], o[0]![1], o[1]![0], o[1]![1], o[2]![0], o[2]![1], o[3]![0], o[3]![1]]
}

/** Aire du quadrilatère (formule du lacet), dans les unités des coins. */
export function aireCadre(c: Cadre, largeur = 1, hauteur = 1): number {
  const p = coinsDe(c)
  let s = 0
  for (let k = 0; k < 4; k++) {
    const a = p[k]!
    const b = p[(k + 1) % 4]!
    s += a[0] * largeur * b[1] * hauteur - b[0] * largeur * a[1] * hauteur
  }
  return Math.abs(s) / 2
}

/** Convexe, dans l'ordre des coins (jamais en miroir), dans la photo et assez grand. */
export function cadreValide(c: Cadre): boolean {
  if (!Array.isArray(c) || c.length !== 8) return false
  if (!c.every((v) => Number.isFinite(v) && v >= 0 && v <= 1)) return false
  const p = coinsDe(c)
  for (let k = 0; k < 4; k++) {
    const a = p[k]!
    const b = p[(k + 1) % 4]!
    const d = p[(k + 2) % 4]!
    const produit = (b[0] - a[0]) * (d[1] - b[1]) - (b[1] - a[1]) * (d[0] - b[0])
    if (!(produit > 0)) return false
  }
  return aireCadre(c) >= AIRE_MIN_CADRE
}

/** Collé aux bords de la photo : rien à recadrer. */
export function cadreEntier(c: Cadre): boolean {
  return c.every((v, i) => Math.abs(v - CADRE_ENTIER[i]!) <= MARGE_CADRE_ENTIER)
}

/**
 * Homographie qui envoie chacun des quatre points `de` sur le point `vers` de même rang ; null
 * si les points sont alignés (pas de transformation possible).
 */
export function homographie(de: readonly Point[], vers: readonly Point[]): Homographie | null {
  // Huit équations linéaires en h0..h7 (h8 = 1), résolues par Gauss avec pivot partiel.
  const m: number[][] = []
  for (let k = 0; k < 4; k++) {
    const [x, y] = de[k]!
    const [X, Y] = vers[k]!
    m.push([x, y, 1, 0, 0, 0, -x * X, -y * X, X])
    m.push([0, 0, 0, x, y, 1, -x * Y, -y * Y, Y])
  }
  for (let col = 0; col < 8; col++) {
    let pivot = col
    for (let l = col + 1; l < 8; l++)
      if (Math.abs(m[l]![col]!) > Math.abs(m[pivot]![col]!)) pivot = l
    if (Math.abs(m[pivot]![col]!) < 1e-12) return null
    ;[m[col], m[pivot]] = [m[pivot]!, m[col]!]
    const ligne = m[col]!
    for (let l = 0; l < 8; l++) {
      const autre = m[l]!
      const f = l === col ? 0 : autre[col]! / ligne[col]!
      if (f === 0) continue
      for (let j = col; j < 9; j++) autre[j] = autre[j]! - f * ligne[j]!
    }
  }
  const h = m.map((l, i) => l[8]! / l[i]!)
  return [h[0]!, h[1]!, h[2]!, h[3]!, h[4]!, h[5]!, h[6]!, h[7]!, 1]
}

export function appliquer(h: Homographie, x: number, y: number): Point {
  const w = h[6] * x + h[7] * y + h[8]
  return [(h[0] * x + h[1] * y + h[2]) / w, (h[3] * x + h[4] * y + h[5]) / w]
}

/**
 * Point de la photo affichée vers la photo telle qu'enregistrée (avant l'orientation EXIF),
 * en fractions : le serveur lit les pixels enregistrés, le téléphone a montré la photo tournée.
 */
export function versPhotoStockee(orientation: number, u: number, v: number): Point {
  switch (orientation) {
    case 2:
      return [1 - u, v]
    case 3:
      return [1 - u, 1 - v]
    case 4:
      return [u, 1 - v]
    case 5:
      return [v, u]
    case 6:
      return [v, 1 - u]
    case 7:
      return [1 - v, 1 - u]
    case 8:
      return [1 - v, u]
    default:
      return [u, v]
  }
}

/**
 * Homographie de la page redressée (carré unité : x de gauche à droite, y de haut en bas)
 * vers la photo enregistrée (fractions de sa largeur et de sa hauteur).
 */
export function redressement(c: Cadre, orientation: number): Homographie | null {
  return homographie(
    CARRE,
    coinsDe(c).map(([u, v]) => versPhotoStockee(orientation, u, v)),
  )
}

/**
 * Taille en pixels de la page redressée, d'après la photo affichée (`largeur` × `hauteur`) :
 * le plus long de chaque paire de côtés opposés, jamais plus de pixels que la photo.
 */
export function dimensionsRedressees(
  c: Cadre,
  largeur: number,
  hauteur: number,
): { largeur: number; hauteur: number } {
  const p = coinsDe(c).map(([x, y]) => [x * largeur, y * hauteur] as const)
  const d = (a: number, b: number) => Math.hypot(p[b]![0] - p[a]![0], p[b]![1] - p[a]![1])
  const l = Math.max(d(0, 1), d(3, 2))
  const h = Math.max(d(0, 3), d(1, 2))
  const plafond = largeur * hauteur
  if (Math.round(l) * Math.round(h) <= plafond)
    return { largeur: Math.max(1, Math.round(l)), hauteur: Math.max(1, Math.round(h)) }
  const f = Math.sqrt(plafond / (l * h))
  return { largeur: Math.max(1, Math.floor(l * f)), hauteur: Math.max(1, Math.floor(h * f)) }
}

/**
 * Pixels de la page redressée (`largeur` × `hauteur`), lus dans `source` par l'homographie
 * `h` (page en carré unité vers fractions de la source), interpolation bilinéaire. Le worker
 * d'image du serveur fait le même calcul sur la photo pleine résolution.
 */
export function echantillonner(
  source: { largeur: number; hauteur: number; donnees: ArrayLike<number> },
  h: Homographie,
  largeur: number,
  hauteur: number,
): Uint8ClampedArray<ArrayBuffer> {
  const { largeur: sl, hauteur: sh, donnees: s } = source
  const sortie = new Uint8ClampedArray(largeur * hauteur * 4)
  for (let j = 0; j < hauteur; j++) {
    const t = (j + 0.5) / hauteur
    for (let i = 0; i < largeur; i++) {
      const u = (i + 0.5) / largeur
      const w = h[6] * u + h[7] * t + h[8]
      const x = ((h[0] * u + h[1] * t + h[2]) / w) * sl - 0.5
      const y = ((h[3] * u + h[4] * t + h[5]) / w) * sh - 0.5
      const x0 = Math.floor(x)
      const y0 = Math.floor(y)
      const fx = x - x0
      const fy = y - y0
      const xa = Math.min(sl - 1, Math.max(0, x0))
      const xb = Math.min(sl - 1, Math.max(0, x0 + 1))
      const ya = Math.min(sh - 1, Math.max(0, y0))
      const yb = Math.min(sh - 1, Math.max(0, y0 + 1))
      const a = (ya * sl + xa) * 4
      const b = (ya * sl + xb) * 4
      const c = (yb * sl + xa) * 4
      const d = (yb * sl + xb) * 4
      const o = (j * largeur + i) * 4
      for (let k = 0; k < 3; k++) {
        const haut = s[a + k]! + (s[b + k]! - s[a + k]!) * fx
        const bas = s[c + k]! + (s[d + k]! - s[c + k]!) * fx
        sortie[o + k] = haut + (bas - haut) * fy
      }
      sortie[o + 3] = 255
    }
  }
  return sortie
}

const cadreLu = z
  .array(z.number())
  .length(8)
  .transform((v) => v as unknown as Cadre)

/**
 * Champ « cadres » d'une requête d'envoi (JSON) : un cadre par fichier, dans l'ordre des
 * fichiers, null pour une photo gardée entière ; null s'il n'y a rien à recadrer.
 */
export function ecrireCadres(cadres: readonly (Cadre | null | undefined)[]): string | null {
  if (!cadres.some(Boolean)) return null
  const arrondi = (v: number) => Math.round(v * 10_000) / 10_000
  return JSON.stringify(cadres.map((c) => (c ? c.map(arrondi) : null)))
}

/**
 * Relit le champ « cadres » pour `n` fichiers. Jamais bloquant : illisible, de mauvaise
 * longueur, invalide ou collé aux bords, un cadre donne la photo entière (null).
 */
export function lireCadres(texte: string | undefined, n: number): (Cadre | null)[] {
  const entiers = Array.from({ length: n }, () => null)
  if (!texte) return entiers
  let brut: unknown
  try {
    brut = JSON.parse(texte)
  } catch {
    return entiers
  }
  if (!Array.isArray(brut) || brut.length !== n) return entiers
  return brut.map((v) => {
    const c = cadreLu.safeParse(v)
    return c.success && cadreValide(c.data) && !cadreEntier(c.data) ? c.data : null
  })
}
